import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute, Link, Navigate, useNavigate } from '@tanstack/react-router'
import { Trash2 } from 'lucide-react'
import { useFieldArray, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { useAdoption } from '@/api/adoption'
import { useIdempotencyKey } from '@/api/idempotency'
import { useCreateOpeningBalance } from '@/api/opening-balances'
import type { OpeningBalanceBody } from '@/api/opening-balances'
import { poolKindOf, poolLabel, usePools } from '@/api/pools'
import type { Pool } from '@/api/pools'
import { useProducts } from '@/api/products'
import { useSalesChannels } from '@/api/sales-channels'
import { useAllStock } from '@/api/stock'
import { useWorkspace } from '@/api/workspace'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { formatMoney } from '@/components/materials/pool-format'
import { openingBalanceErrorMessage } from '@/components/opening-balances/error-message'
import { formatBusinessDate } from '@/components/opening-balances/format'
import { VariantForm } from '@/components/opening-balances/variant-form'
import type { VariantItem } from '@/components/opening-balances/variant-form'
import { WipForm } from '@/components/opening-balances/wip-form'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

// Create an Opening balance (UI-SCREENS I-13) for the materials location (#65) or, with
// `?location=`, for the WIP location (pre-adoption parts, #139), Home or one Sales Channel
// (Finished Goods, qty only, 05e #173 — `?location=channel&channelId=`). Phone first: one card
// per line, 44px targets, numeric keyboards. The date is the Workspace adoption date and is
// shown, not editable — the server refuses anything else (worked-examples M-8 c).
const searchSchema = z.object({
  location: z.enum(['wip', 'home', 'channel']).optional().catch(undefined),
  channelId: z.coerce.number().int().positive().optional().catch(undefined),
})

export const Route = createFileRoute('/w/$slug/settings/opening-balances/new')({
  component: NewOpeningBalance,
  validateSearch: searchSchema,
  staticData: { breadcrumb: 'settings.openingBalances.form.title' },
})

function NewOpeningBalance() {
  const { t } = useTranslation()
  const { slug } = Route.useParams()
  const { location, channelId } = Route.useSearch()
  const wip = location === 'wip'
  const finishedGoods = location === 'home' || location === 'channel'
  const workspace = useWorkspace(slug)
  const adoption = useAdoption(slug)
  const pools = usePools(slug)
  const products = useProducts(slug)
  // Archived channels take no new stock, so only the active ones are offered here.
  const channels = useSalesChannels(slug, { enabled: !wip })
  // The Variants the lines pick from, with the stock item id the API takes.
  const stock = useAllStock(slug, { enabled: finishedGoods })

  if (workspace.data?.role !== 'owner') return <Navigate to="/w/$slug" params={{ slug }} replace />

  const filaments = (pools.data ?? []).filter((pool) => pool.kind === 'filament' && !pool.archived_at)
  const activeProducts = (products.data ?? []).filter((product) => !product.archived_at)
  const activeChannels = (channels.data ?? []).filter((channel) => !channel.archived_at)
  const channel = activeChannels.find((row) => row.id === channelId) ?? activeChannels[0]
  const variantItems: VariantItem[] = (stock.data?.items ?? []).map((row) => ({
    id: row.stock.stock_item_id,
    sku: row.variant.sku,
    name: row.variant.name,
  }))
  const pending = adoption.isPending || pools.isPending || (wip && products.isPending) || (finishedGoods && stock.isPending)
  const failed =
    adoption.isError ||
    !adoption.data ||
    pools.isError ||
    !pools.data ||
    (wip && (products.isError || !products.data)) ||
    (finishedGoods && stock.isError)

  return (
    <section className="flex max-w-2xl flex-col gap-4">
      <Link to="/w/$slug/settings/opening-balances" params={{ slug }} className="text-sm underline-offset-4 hover:underline">
        {t('settings.openingBalances.back')}
      </Link>
      <h1 className="font-heading text-xl font-semibold">{t('settings.openingBalances.form.title')}</h1>
      {/* One document per location (owner decision 2026-09-23, Q10): the Owner enters the
          materials location, WIP, Home and each channel in turn. */}
      <div className="grid grid-cols-2 gap-2">
        <LocationButton slug={slug} active={location === undefined} label={t('settings.openingBalances.locations.materials')} />
        <LocationButton slug={slug} active={wip} search={{ location: 'wip' }} label={t('settings.openingBalances.locations.wip')} />
        <LocationButton
          slug={slug}
          active={location === 'home'}
          search={{ location: 'home' }}
          label={t('settings.openingBalances.locations.home')}
        />
        {activeChannels.map((row) => (
          <LocationButton
            key={row.id}
            slug={slug}
            active={location === 'channel' && channel?.id === row.id}
            search={{ location: 'channel', channelId: row.id }}
            label={row.name}
          />
        ))}
      </div>

      {pending ? (
        <LoadingSkeleton variant="form" rows={4} />
      ) : failed || !adoption.data || !pools.data ? (
        <p role="alert" className="text-sm text-destructive">
          {t('errors.generic')}
        </p>
      ) : !adoption.data.adopted_on ? (
        <Missing slug={slug} message={t('settings.openingBalances.noAdoptionDate')} to="workspace" />
      ) : finishedGoods ? (
        location === 'channel' && !channel ? (
          <Missing slug={slug} message={t('settings.openingBalances.variant.noChannels')} to="channels" />
        ) : variantItems.length === 0 ? (
          <Missing slug={slug} message={t('settings.openingBalances.variant.noVariants')} to="products" />
        ) : (
          <VariantForm
            key={`${location}:${channel?.id ?? ''}`}
            slug={slug}
            adoptedOn={adoption.data.adopted_on}
            location={location}
            salesChannelId={location === 'channel' ? channel?.id : undefined}
            items={variantItems}
          />
        )
      ) : wip ? (
        activeProducts.length === 0 ? (
          <Missing slug={slug} message={t('settings.openingBalances.wip.noProducts')} to="products" />
        ) : filaments.length === 0 ? (
          <Missing slug={slug} message={t('settings.openingBalances.form.noPools')} to="materials" />
        ) : (
          <WipForm slug={slug} adoptedOn={adoption.data.adopted_on} products={activeProducts} pools={filaments} />
        )
      ) : pools.data.length === 0 ? (
        <Missing slug={slug} message={t('settings.openingBalances.form.noPools')} to="materials" />
      ) : (
        <LineForm slug={slug} adoptedOn={adoption.data.adopted_on} pools={pools.data} />
      )}
    </section>
  )
}

function LocationButton({
  slug,
  active,
  search,
  label,
}: {
  slug: string
  active: boolean
  search?: { location: 'wip' | 'home' | 'channel'; channelId?: number }
  label: string
}) {
  return (
    <Button asChild variant={active ? 'default' : 'outline'} className="h-auto min-h-11 whitespace-normal">
      <Link to="/w/$slug/settings/opening-balances/new" params={{ slug }} search={search ?? {}}>
        {label}
      </Link>
    </Button>
  )
}

function Missing({
  slug,
  message,
  to,
}: {
  slug: string
  message: string
  to: 'workspace' | 'materials' | 'products' | 'channels'
}) {
  const { t } = useTranslation()

  return (
    <Card>
      <CardContent className="flex flex-col items-start gap-3 py-4">
        <p role="status" className="text-sm">
          {message}
        </p>
        <Button asChild variant="outline" className="h-11">
          {to === 'workspace' ? (
            <Link to="/w/$slug/settings/workspace" params={{ slug }}>
              {t('settings.openingBalances.goToAdoption')}
            </Link>
          ) : to === 'channels' ? (
            <Link to="/w/$slug/channels" params={{ slug }}>
              {t('settings.openingBalances.variant.goToChannels')}
            </Link>
          ) : to === 'products' ? (
            <Link to="/w/$slug/products" params={{ slug }}>
              {t('settings.openingBalances.wip.goToProducts')}
            </Link>
          ) : (
            <Link to="/w/$slug/materials" params={{ slug }}>
              {t('settings.openingBalances.form.goToPools')}
            </Link>
          )}
        </Button>
      </CardContent>
    </Card>
  )
}

const EMPTY_LINE = { poolId: '', poolKind: '', qty: '', costPerUnit: '' }

function LineForm({ slug, adoptedOn, pools }: { slug: string; adoptedOn: string; pools: Pool[] }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const create = useCreateOpeningBalance()
  const idempotency = useIdempotencyKey()

  // Every stored number is an integer (domain-rules): qty > 0, whole-Rp cost per unit ≥ 0. The
  // server checks the same rules; these are only for a quicker answer.
  const wholeNumber = (message: string, min: number) =>
    z.string().refine((value) => /^\d+$/.test(value) && Number(value) >= min, { message })

  const formSchema = z.object({
    note: z.string(),
    lines: z
      .array(
        z.object({
          poolId: z.string().min(1, { message: t('settings.openingBalances.form.required') }),
          poolKind: z.string(),
          qty: wholeNumber(t('settings.openingBalances.form.qtyInvalid'), 1),
          costPerUnit: wholeNumber(t('settings.openingBalances.form.costInvalid'), 0),
        }),
      )
      .min(1)
      // One line per Pool: the ledger takes one movement per Pool per record (decided Q-5).
      .refine((lines) => new Set(lines.map((line) => line.poolId)).size === lines.length, {
        message: t('settings.openingBalances.form.duplicatePool'),
      }),
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { note: '', lines: [EMPTY_LINE] },
  })
  const lines = useFieldArray({ control: form.control, name: 'lines' })
  // useWatch rather than form.watch: it returns a value the React compiler can memoize.
  const watched = useWatch({ control: form.control, name: 'lines' })

  const onSubmit = form.handleSubmit((values) => {
    const body: OpeningBalanceBody = {
      opening_balance: {
        occurred_on: adoptedOn,
        note: values.note.trim() === '' ? null : values.note.trim(),
        lines: values.lines.map((line) => ({
          pool_id: Number(line.poolId),
          pool_kind: line.poolKind,
          qty: Number(line.qty),
          cost_per_unit: Number(line.costPerUnit),
        })),
      },
    }
    create.mutate(
      { params: { path: { slug }, header: idempotency.headerFor(body) }, body },
      {
        onSuccess: (data) =>
          navigate({
            to: '/w/$slug/settings/opening-balances/$openingBalanceId',
            params: { slug, openingBalanceId: String(data.id) },
          }),
        onSettled: (_data, error) => idempotency.settle(error),
      },
    )
  })

  const total = watched.reduce((sum, line) => sum + lineValue(line), 0)

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
      <p role="alert" aria-live="assertive" className="text-sm text-destructive empty:hidden">
        {create.isError ? openingBalanceErrorMessage(t, create.error) : ''}
      </p>

      <div className="flex flex-col gap-1">
        <span className="text-sm font-medium">{t('settings.openingBalances.form.date')}</span>
        <span>{formatBusinessDate(adoptedOn)}</span>
        <p className="text-sm text-muted-foreground">{t('settings.openingBalances.form.dateHint')}</p>
      </div>

      <div className="flex flex-col gap-3">
        {lines.fields.map((field, index) => (
          <Card key={field.id}>
            <CardContent className="flex flex-col gap-3 py-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor={`pool-${index}`}>{t('settings.openingBalances.form.pool')}</Label>
                {/* A native select on purpose: a Workspace can hold dozens of Pools, and on a
                    phone the OS picker beats a scrolling popover. */}
                <select
                  id={`pool-${index}`}
                  className="h-11 w-full rounded-lg border border-input bg-transparent px-3 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
                  {...form.register(`lines.${index}.poolId`, {
                    onChange: (event) => form.setValue(`lines.${index}.poolKind`, poolKindOf(pools, event.target.value)),
                  })}
                >
                  <option value="">{t('settings.openingBalances.form.selectPool')}</option>
                  {pools.map((pool) => (
                    <option key={pool.id} value={String(pool.id)}>
                      {poolLabel(pool)}
                    </option>
                  ))}
                </select>
                <FieldError message={form.formState.errors.lines?.[index]?.poolId?.message} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-2">
                  <Label htmlFor={`qty-${index}`}>{t('settings.openingBalances.form.qty')}</Label>
                  <Input
                    id={`qty-${index}`}
                    inputMode="numeric"
                    className="h-11"
                    {...form.register(`lines.${index}.qty`)}
                  />
                  <FieldError message={form.formState.errors.lines?.[index]?.qty?.message} />
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor={`cost-${index}`}>{t('settings.openingBalances.form.costPerUnit')}</Label>
                  <Input
                    id={`cost-${index}`}
                    inputMode="numeric"
                    className="h-11"
                    {...form.register(`lines.${index}.costPerUnit`)}
                  />
                  <FieldError message={form.formState.errors.lines?.[index]?.costPerUnit?.message} />
                </div>
              </div>

              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-muted-foreground">
                  {t('settings.openingBalances.form.lineValue')}: Rp {formatMoney(lineValue(watched[index]))}
                </span>
                {lines.fields.length > 1 ? (
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-11 text-destructive"
                    onClick={() => lines.remove(index)}
                  >
                    <Trash2 aria-hidden="true" />
                    {t('settings.openingBalances.form.removeLine')}
                  </Button>
                ) : null}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <FieldError message={form.formState.errors.lines?.root?.message ?? form.formState.errors.lines?.message} />

      <Button type="button" variant="outline" className="h-11 self-start" onClick={() => lines.append(EMPTY_LINE)}>
        {t('settings.openingBalances.form.addLine')}
      </Button>

      <div className="flex flex-col gap-2">
        <Label htmlFor="note">{t('settings.openingBalances.form.note')}</Label>
        <Input id="note" className="h-11" {...form.register('note')} />
      </div>

      <p className="text-base font-medium">
        {t('settings.openingBalances.form.total')}: Rp {formatMoney(total)}
      </p>

      <div className="flex flex-wrap gap-2">
        <Button type="submit" className="h-11" disabled={create.isPending}>
          {t(create.isPending ? 'settings.openingBalances.form.saving' : 'settings.openingBalances.form.save')}
        </Button>
        <Button asChild variant="ghost" className="h-11">
          <Link to="/w/$slug/settings/opening-balances" params={{ slug }}>
            {t('settings.openingBalances.form.cancel')}
          </Link>
        </Button>
      </div>
    </form>
  )
}

// qty × cost per unit, exact (decided Q-7). Both are whole numbers, so this stays an integer.
function lineValue(line: { qty: string; costPerUnit: string } | undefined): number {
  const qty = Number(line?.qty)
  const cost = Number(line?.costPerUnit)
  return Number.isInteger(qty) && Number.isInteger(cost) && qty > 0 && cost >= 0 ? qty * cost : 0
}

function FieldError({ message }: { message?: string }) {
  return (
    <p role="alert" className="text-sm text-destructive empty:hidden">
      {message ?? ''}
    </p>
  )
}
